import ConfirmDialog from '../components/ConfirmDialog';
import React, { useState, useEffect } from 'react';
import { toast } from '../components/Toast';
import Layout from '../components/Layout';
import api from '../services/api';

const statusColor = { draft:'#6B7280', sent:'#2563EB', paid:'#16A34A', overdue:'#DC2626', cancelled:'#9CA3AF', credited:'#7C3AED', adjusted:'#D97706', void:'#9CA3AF' };
const statusBg   = { draft:'#F3F4F6', sent:'#EFF6FF', paid:'#F0FDF4', overdue:'#FEF2F2', cancelled:'#F9FAFB', credited:'#F5F3FF', adjusted:'#FFFBEB', void:'#F9FAFB' };

export default function Invoicing() {
  const [invoices, setInvoices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const [stats, setStats]         = useState({ total:0, sent:0, paid:0, overdue:0, totalAmount:0, paidAmount:0, pendingAmount:0 });
  const [showModal, setShowModal]   = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [clients, setClients]       = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [billableProducts, setBillableProducts] = useState([]);
  const [saving, setSaving]         = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingMonth, setBillingMonth]         = useState(new Date().toISOString().substring(0,7));
  const [billingPreview, setBillingPreview]     = useState(null);
  const [billingRunning, setBillingRunning]     = useState(false);
  const [billingConfirmed, setBillingConfirmed] = useState(false);
  const [selectedInvoice, setSelectedInvoice]   = useState(null);
  const [showDetailModal, setShowDetailModal]   = useState(false);
  const [invoiceDetailTab, setInvoiceDetailTab]   = useState('details');
  const [invoiceHistory, setInvoiceHistory]       = useState([]);
  const [historyLoading, setHistoryLoading]       = useState(false);
  const [detailLoading, setDetailLoading]       = useState(false);
  const [markingPaid, setMarkingPaid]           = useState(false);
  const [sending, setSending]                   = useState(false);
  const [showCMModal, setShowCMModal]           = useState(false);
  const [cmDetailModal, setCmDetailModal]         = useState(null); // selected CM
  const [cmDetailTab, setCmDetailTab]             = useState('details');
  const [cmHistory, setCmHistory]                 = useState([]);
  const [cmHistoryLoading, setCmHistoryLoading]   = useState(false);
  const [cmInvoice, setCmInvoice]               = useState(null);
  const [cmForm, setCmForm]                     = useState({ reason_type: 'error_invoice', reason_notes: '', is_full_credit: true, amount: '', disposition: 'apply_next' });
  const [cmSaving, setCmSaving]                 = useState(false);
  const [showRefundModal, setShowRefundModal]   = useState(false);
  const [refundCM, setRefundCM]                 = useState(null);
  const [refundForm, setRefundForm]             = useState({ refund_method: 'check', refund_notes: '' });
  const [clientCredits, setClientCredits]       = useState({ data: [], total: 0 });
  const [clientSearch, setClientSearch]         = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [taxCategories, setTaxCategories]       = useState([]);
  const [invoiceCountry, setInvoiceCountry]     = useState('usa');
  const [sendModal, setSendModal]               = useState(null); // {inv}
  const [sendRecipients, setSendRecipients]     = useState([]); // [{email, label, checked}]
  const [sendExtraEmail, setSendExtraEmail]     = useState('');
  const [sendingModal, setSendingModal]         = useState(false);
  const [voidModal, setVoidModal]               = useState(null); // {inv}
  const [voidForm, setVoidForm]                 = useState({ reason:'', notes:'' });
  const [cancelModal, setCancelModal]           = useState(null); // {inv}
  const [cancelForm, setCancelForm]             = useState({ reason:'', notes:'' });
  const [applyCredit, setApplyCredit]           = useState(false);
  const [creditMemos, setCreditMemos]           = useState([]);
  const [activeTab, setActiveTab]               = useState('all');
  const [pdfLoading, setPdfLoading]             = useState(false);
  const [pdfUrl, setPdfUrl]                     = useState(null);
  const [showPdfModal, setShowPdfModal]         = useState(false);
  const [payForm, setPayForm]                   = useState({ payment_method:'check', paid_amount:'' });
  const [form, setForm]             = useState({
    client_id: '', service_agreement_id: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', notes: '', items: []
  });

  useEffect(() => { fetchInvoices(); fetchStats(); fetchCreditMemos(); fetchTaxCategories(); }, []);

  const fetchTaxCategories = async () => {
    try {
      // Get company invoice profile country first
      const settingsRes = await api.get('/companies/settings').catch(() => null);
      const country = settingsRes?.data?.settings?.invoice_profile?.country || 'usa';
      setInvoiceCountry(country);
      const res = await api.get('/tax-categories?country=' + country);
      setTaxCategories(res.data.data || []);
    } catch(e) { console.error('fetchTaxCategories', e); }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await api.get('/invoices');
      setInvoices(res.data.data || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setInvoices([]);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/invoices/stats');
      const d = res.data.data || {};
      setStats({
        total:         parseInt(d.total || 0),
        sent:          parseInt(d.sent_count || 0),
        paid:          parseInt(d.paid_count || 0),
        overdue:       parseInt(d.overdue_count || 0),
        totalAmount:   parseFloat(d.total_amount || 0),
        paidAmount:    parseFloat(d.paid_amount || 0),
        pendingAmount: parseFloat(d.pending_amount || 0),
      });
    } catch (err) { console.error('Stats error:', err); }
  };

  const runBillingPreview = async () => {
    setBillingRunning(true); setBillingPreview(null); setBillingConfirmed(false);
    try {
      const res = await api.post('/invoices/run-billing', { billing_month: billingMonth, preview: true });
      setBillingPreview(res.data);
    } catch (e) { toast.error('Error: ' + (e.response?.data?.message || e.message)); }
    setBillingRunning(false);
  };

  const executeBilling = async () => {
    setConfirmDialog({ message: `Generate ${billingPreview?.to_create || 0} invoice(s) for ${billingPreview?.month_label}?`, confirmText: 'Generate Invoices', confirmColor: '#2D5C3F', icon: '⚡', onConfirm: async () => {
      setConfirmDialog(null);
    try {
      const res = await api.post('/invoices/run-billing', { billing_month: billingMonth, preview: false });
      setBillingConfirmed(true);
      setBillingPreview(res.data);
      fetchInvoices(); fetchStats();
    } catch (e) { toast.error('Error: ' + (e.response?.data?.message || e.message)); }
    setBillingRunning(false);
    }});
  };

  const openDetail = async (inv) => {
    setDetailLoading(true); setShowDetailModal(true); setPayForm({ payment_method:'check', paid_amount: inv.total_amount }); setInvoiceDetailTab('details'); setInvoiceHistory([]);
    try {
      const res = await api.get('/invoices/' + inv.id);
      setSelectedInvoice(res.data.data);
    } catch (e) { toast.error('Error loading invoice'); setShowDetailModal(false); }
    setDetailLoading(false);
  };

  const openSendModal = async (inv) => {
    // Load client billing emails
    const recipients = [];
    // Main billing email
    if (inv.client_billing_email || inv.client_email) {
      recipients.push({ email: inv.client_billing_email || inv.client_email, label: 'Billing Email', checked: true });
    }
    // Additional billing emails
    try {
      const res = await api.get(`/clients/${inv.client_id}/billing-emails`);
      for (const be of (res.data.data || [])) {
        recipients.push({ email: be.email, label: be.label || 'Additional', checked: true });
      }
    } catch(e) {}
    setSendRecipients(recipients);
    setSendExtraEmail('');
    setSendModal(inv);
  };

  const submitSendInvoice = async () => {
    const toEmails = sendRecipients.filter(r => r.checked).map(r => r.email);
    if (sendExtraEmail && sendExtraEmail.includes('@')) toEmails.push(sendExtraEmail);
    if (toEmails.length === 0) { toast.info('Select at least one recipient'); return; }
    setSendingModal(true);
    try {
      await api.post('/invoices/' + sendModal.id + '/send', { to: toEmails });
      toast.success(`Invoice sent to ${toEmails.length} recipient${toEmails.length > 1 ? 's' : ''}`);
      const res = await api.get('/invoices/' + sendModal.id);
      setSelectedInvoice(res.data.data);
      fetchInvoices(); fetchStats();
      setSendModal(null);
    } catch(e) { toast.error('Error sending: ' + (e.response?.data?.message || e.message)); }
    setSendingModal(false);
  };

  const sendInvoice = async (id, toEmail) => {
    setSending(true);
    try {
      await api.post('/invoices/' + id + '/send', { to: toEmail });
      toast.success('Invoice sent to ' + toEmail);
      const res = await api.get('/invoices/' + id);
      setSelectedInvoice(res.data.data);
      fetchInvoices(); fetchStats();
    } catch (e) { toast.error('Error sending: ' + (e.response?.data?.message || e.message)); }
    setSending(false);
  };

  const markPaid = async (id) => {
    if (!payForm.paid_amount) { toast.success('Enter payment amount'); return; }
    setMarkingPaid(true);
    try {
      await api.put('/invoices/' + id + '/status', {
        status: 'paid', payment_method: payForm.payment_method,
        paid_amount: parseFloat(payForm.paid_amount)
      });
      const res = await api.get('/invoices/' + id);
      setSelectedInvoice(res.data.data);
      fetchInvoices(); fetchStats();
    } catch (e) { toast.error('Error: ' + (e.response?.data?.message || e.message)); }
    setMarkingPaid(false);
  };

  const fetchCreditMemos = async () => {
    try {
      const res = await api.get('/credit-memos');
      setCreditMemos(res.data.data || []);
    } catch(e) { console.error(e); }
  };

  const openCreditMemo = (inv) => {
    setCmInvoice(inv);
    setCmForm({ reason_type: 'error_invoice', reason_notes: '', is_full_credit: true, amount: inv.total_amount, disposition: 'apply_next' });
    setShowCMModal(true);
  };

  const createCreditMemo = async () => {
    if (cmSaving) return; // prevent double submit
    if (!cmForm.reason_notes.trim()) { toast.error('Please enter a reason'); return; }
    if (!cmForm.is_full_credit && (!cmForm.amount || parseFloat(cmForm.amount) <= 0)) {
      toast.error('Please enter a valid amount'); return;
    }
    setCmSaving(true);
    try {
      await api.post('/credit-memos', {
        invoice_id: cmInvoice.id,
        reason_type: cmForm.reason_type,
        reason_notes: cmForm.reason_notes,
        is_full_credit: cmForm.is_full_credit,
        amount: cmForm.is_full_credit ? cmInvoice.total_amount : parseFloat(cmForm.amount),
        disposition: cmForm.disposition,
      });
      toast.success('Credit memo issued successfully');
      setShowCMModal(false);
      fetchInvoices();
      fetchCreditMemos();
    } catch(e) { toast.error('Error: ' + (e.response?.data?.message || e.message)); }
    setCmSaving(false);
  };

  const reactivateInvoice = (inv) => {
    setConfirmDialog({
      message: `Reactivate invoice ${inv.invoice_number}? It will return to 'Sent' status. You can then mark it as paid or issue a credit memo.`,
      confirmText: 'Reactivate',
      confirmColor: '#2563EB',
      icon: '🔄',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.put(`/invoices/${inv.id}/status`, { status: 'sent' });
          toast.success(`Invoice ${inv.invoice_number} reactivated`);
          fetchInvoices();
        } catch(e) { toast.error(e.response?.data?.message || e.message); }
      }
    });
  };

  const voidCreditMemo = (cm) => {
    setConfirmDialog({
      message: `Void credit memo ${cm.cm_number}? It will be marked as void and cannot be used. The original invoice status will not change.`,
      confirmText: 'Void CM',
      confirmColor: '#6B7280',
      icon: '⚠️',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.post(`/credit-memos/${cm.id}/void`);
          toast.success(`Credit memo ${cm.cm_number} voided`);
          fetchCreditMemos();
        } catch(e) { toast.error(e.response?.data?.message || e.message); }
      }
    });
  };

  const voidInvoice = (inv) => {
    setVoidForm({ reason:'', notes:'' });
    setVoidModal(inv);
  };
  const submitVoid = async () => {
    if (!voidForm.reason) { toast.info('Please select a reason'); return; }
    try {
      await api.delete(`/invoices/${voidModal.id}`, { data: { void_reason: voidForm.reason, void_notes: voidForm.notes } });
      toast.success(`Invoice ${voidModal.invoice_number} voided`);
      setVoidModal(null);
      fetchInvoices(); fetchStats();
    } catch(e) { toast.error(e.response?.data?.message || 'Error voiding invoice'); }
  };

  const cancelInvoice = (inv) => {
    setCancelForm({ reason:'', notes:'' });
    setCancelModal(inv);
  };
  const submitCancel = async () => {
    if (!cancelForm.reason) { toast.info('Please select a reason'); return; }
    try {
      await api.put(`/invoices/${cancelModal.id}/status`, { status: 'cancelled', cancel_reason: cancelForm.reason, cancel_notes: cancelForm.notes });
      toast.success(`Invoice ${cancelModal.invoice_number} cancelled`);
      setCancelModal(null);
      fetchInvoices(); fetchStats();
    } catch(e) { toast.error(e.response?.data?.message || e.message); }
  };

  const markRefunded = async () => {
    if (!refundForm.refund_method) { toast.error('Select refund method'); return; }
    try {
      await api.post(`/credit-memos/${refundCM.id}/refund`, refundForm);
      toast.success('Credit memo marked as refunded');
      setShowRefundModal(false);
      fetchCreditMemos();
    } catch(e) { toast.error(e.response?.data?.message || e.message); }
  };

  const checkClientCredits = async (clientId) => {
    if (!clientId) return;
    try {
      const res = await api.get(`/credit-memos/available/${clientId}`);
      setClientCredits({ data: res.data.data || [], total: res.data.total_available || 0 });
    } catch(e) { setClientCredits({ data: [], total: 0 }); }
  };

  const applyCreditToNewInvoice = async (invoiceId) => {
    if (!clientCredits.data.length) return;
    try {
      for (const cm of clientCredits.data) {
        await api.post(`/credit-memos/${cm.id}/apply/${invoiceId}`);
      }
      toast.success('Credits applied to invoice');
      fetchInvoices(); fetchCreditMemos();
      setApplyCredit(false);
      setClientCredits({ data: [], total: 0 });
    } catch(e) { toast.error(e.response?.data?.message || e.message); }
  };

  const downloadInvoicePdf = async (inv) => {
    if (!inv) return;
    setPdfLoading(true);
    setPdfUrl(null);
    setShowPdfModal(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`https://api.safecorepest.com/api/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      toast.error('Error generating PDF: ' + err.message);
      setShowPdfModal(false);
    } finally { setPdfLoading(false); }
  };

  const handleOpenNewInvoice = async (clientId) => {
    await fetchTaxCategories(); // always get fresh rates including company overrides
    setShowModal(true);
    if (clientId) await checkClientCredits(clientId);
  };

  const fetchModalData = async () => {
    try {
      const [cRes, aRes, pRes] = await Promise.all([
        api.get('/clients'),
        api.get('/service-agreements'),
        api.get('/products'),
      ]);
      setClients(cRes.data.data || cRes.data || []);
      setAgreements(aRes.data.data || aRes.data || []);
      const prods = pRes.data.products || pRes.data || [];
      setBillableProducts(prods.filter(p => p.is_billable && p.unit_price > 0));
    } catch (err) { console.error('Modal data error:', err); }
  };

  const openModal = () => {
    fetchModalData();
    setForm({ client_id: '', service_agreement_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', notes: '', items: [] });
    setShowModal(true);
  };

  const calcDueDate = (invoiceDate, billingTerms) => {
    if (!invoiceDate) return '';
    const date = new Date(invoiceDate + 'T00:00:00');
    const terms = (billingTerms || '').toLowerCase();
    if (terms === 'net_15' || terms === 'net 15')      date.setDate(date.getDate() + 15);
    else if (terms === 'net_30' || terms === 'net 30') date.setDate(date.getDate() + 30);
    else if (terms === 'net_45' || terms === 'net 45') date.setDate(date.getDate() + 45);
    else if (terms === 'net_60' || terms === 'net 60') date.setDate(date.getDate() + 60);
    else if (terms === 'due_on_completion' || terms === 'due on receipt') date.setDate(date.getDate());
    else date.setDate(date.getDate() + 30); // default Net 30
    return date.toISOString().split('T')[0];
  };

  const onClientChange = async (clientId) => {
    const client = clients.find(c => String(c.id) === String(clientId));
    const ag = agreements.find(a => String(a.client_id) === String(clientId) ||
      (client && a.client_name === client.name));
    const newItems = ag ? [{ description: ag.service_type + ' — ' + ag.frequency, quantity: 1, unit_price: parseFloat(ag.price_per_service||0), item_type: 'service', product_id: null, tax_category_id: null }] : [];
    const autodue = ag ? calcDueDate(form.invoice_date || new Date().toISOString().split('T')[0], ag.billing_terms) : '';
    setForm(f => ({ ...f, client_id: clientId, service_agreement_id: ag ? ag.id : '', items: newItems, due_date: autodue }));
    // Check for available credits
    setApplyCredit(false);
    if (clientId) await checkClientCredits(clientId);
    else setClientCredits({ data: [], total: 0 });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, unit_price: 0, item_type: 'service', product_id: null, tax_category_id: null }] }));
  const addBillableProduct = (prod) => {
    setForm(f => ({ ...f, items: [...f.items, { description: prod.name, quantity: 1, unit_price: parseFloat(prod.unit_price), item_type: 'material', product_id: prod.id, tax_category_id: null }] }));
  };
  const updateItem = (idx, field, val) => setForm(f => { const items = [...f.items]; items[idx] = {...items[idx], [field]: val}; return {...f, items}; });
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_,i) => i !== idx) }));
  const getTotal = () => {
    return form.items.reduce((s, it) => {
      const lineTotal = parseFloat(it.quantity||0) * parseFloat(it.unit_price||0);
      const tc = taxCategories.find(t => t.id === it.tax_category_id);
      const taxRate = (tc && !tc.is_exempt && !tc.is_zero_rated) ? parseFloat(tc.rate||0) : 0;
      return s + lineTotal + (lineTotal * taxRate / 100);
    }, 0);
  };
  const getLineTax = (it) => {
    const tc = taxCategories.find(t => t.id === it.tax_category_id);
    const taxRate = (tc && !tc.is_exempt && !tc.is_zero_rated) ? parseFloat(tc.rate||0) : 0;
    return parseFloat(it.quantity||0) * parseFloat(it.unit_price||0) * taxRate / 100;
  };

  const handleSave = async (sendAfter = false) => {
    if (!form.client_id) { toast.info('Please select a client'); return; }
    if (form.items.length === 0) { toast.info('Add at least one line item'); return; }
    setSaving(true);
    try {
      const res = await api.post('/invoices', {
        client_id: parseInt(form.client_id),
        service_agreement_id: form.service_agreement_id ? parseInt(form.service_agreement_id) : null,
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        notes: form.notes || null,
        items: form.items.map(it => ({ ...it, quantity: parseFloat(it.quantity||1), unit_price: parseFloat(it.unit_price||0) }))
      });
      // Apply available credits if checkbox is checked
      if (applyCredit && clientCredits.data.length > 0) {
        for (const cm of clientCredits.data) {
          try {
            await api.post(`/credit-memos/${cm.id}/apply/${res.data.data.id}`);
          } catch(e) { console.error('Error applying credit:', e); }
        }
        toast.success('Credits applied to invoice');
        setApplyCredit(false);
        setClientCredits({ data: [], total: 0 });
      }
      if (sendAfter) await api.post('/invoices/' + res.data.data.id + '/send');
      setShowModal(false);
      fetchInvoices();
      fetchStats();
      fetchCreditMemos();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.message || err.message));
    }
    setSaving(false);
  };

  const fmt = (n) => '$' + parseFloat(n||0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—';

  const filtered = (() => {
    const active = invoices.filter(i => i.status !== 'void' && i.status !== 'cancelled');
    if (filter === 'all') return active;
    if (filter === 'void') return invoices.filter(i => i.status === 'void');
    if (filter === 'cancelled') return invoices.filter(i => i.status === 'cancelled');
    return active.filter(i => i.status === filter);
  })();

  const statCards = [
    { label: 'Total Invoiced',  value: fmt(stats.totalAmount),   color: '#1A3A2A', sub: stats.total + ' invoices' },
    { label: 'Collected',       value: fmt(stats.paidAmount),    color: '#16A34A', sub: stats.paid + ' paid' },
    { label: 'Pending',         value: fmt(stats.pendingAmount), color: '#2563EB', sub: stats.sent + ' sent' },
    { label: 'Overdue',         value: fmt(0),                   color: '#DC2626', sub: stats.overdue + ' invoices' },
  ];

  return (
    <Layout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
          <div>
            <h1 style={{ margin:0, color:'#1A3A2A', fontSize:'24px', fontWeight:700 }}>Invoicing</h1>
            <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:'14px' }}>Manage client invoices and payments</p>
          </div>
          <div style={{ display:'flex', gap:'10px' }}>
            <button style={{ background:'#1565C0', color:'white', border:'none', borderRadius:'8px', padding:'10px 20px', fontSize:'14px', fontWeight:600, cursor:'pointer' }}
              onClick={() => { setShowBillingModal(true); setBillingPreview(null); setBillingConfirmed(false); }}>
              &#9889; Run Monthly Billing
            </button>
            <button style={{ background:'#1A3A2A', color:'white', border:'none', borderRadius:'8px', padding:'10px 20px', fontSize:'14px', fontWeight:600, cursor:'pointer' }}
              onClick={openModal}>
              + New Invoice
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px', marginBottom:'24px' }}>
          {statCards.map((c,i) => (
            <div key={i} style={{ background:'white', borderRadius:'12px', padding:'20px', boxShadow:'0 1px 4px rgba(0,0,0,0.08)', borderLeft:'4px solid ' + c.color }}>
              <div style={{ fontSize:'13px', color:'#6B7280', fontWeight:500 }}>{c.label}</div>
              <div style={{ fontSize:'24px', fontWeight:700, color:c.color, margin:'6px 0 2px' }}>{c.value}</div>
              <div style={{ fontSize:'12px', color:'#9CA3AF' }}>{c.sub}</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'16px' }}>
          {['all','draft','sent','paid','overdue'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding:'6px 16px', borderRadius:'20px', border:'1px solid', fontSize:'13px', fontWeight:500, cursor:'pointer',
                borderColor: filter===f ? '#1A3A2A' : '#E5E7EB',
                background:  filter===f ? '#1A3A2A' : 'white',
                color:       filter===f ? 'white'   : '#374151' }}>
              {f.charAt(0).toUpperCase()+f.slice(1)}
              {f !== 'all' && invoices.filter(i=>i.status===f).length > 0 &&
                <span style={{ marginLeft:'6px', background: filter===f?'rgba(255,255,255,0.3)':'#E5E7EB', borderRadius:'10px', padding:'1px 7px', fontSize:'11px' }}>
                  {invoices.filter(i=>i.status===f).length}
                </span>}
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={{ background:'white', borderRadius:'12px', boxShadow:'0 1px 4px rgba(0,0,0,0.08)', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:'60px', textAlign:'center', color:'#9CA3AF' }}>Loading invoices...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:'60px', textAlign:'center' }}>
              <div style={{ fontSize:'48px', marginBottom:'12px' }}>💵</div>
              <div style={{ fontSize:'16px', fontWeight:600, color:'#374151' }}>No invoices yet</div>
              <div style={{ fontSize:'14px', color:'#9CA3AF', marginTop:'4px' }}>Invoices will be generated automatically when service orders are completed</div>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                  {['Invoice #','Client','Service','Date','Due Date','Amount','Status','Actions'].map(h => (
                    <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:'12px', fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv, idx) => (
                  <tr key={inv.id} style={{ borderBottom:'1px solid #F3F4F6', background: idx%2===0?'white':'#FAFAFA' }}>
                    <td style={{ padding:'12px 16px', fontWeight:600, color:'#1A3A2A', fontSize:'14px' }}>{inv.invoice_number}</td>
                    <td style={{ padding:'12px 16px', fontSize:'14px', color:'#374151' }}>{inv.client_name || '—'}</td>
                    <td style={{ padding:'12px 16px', fontSize:'13px', color:'#6B7280' }}>{inv.service_type || '—'}</td>
                    <td style={{ padding:'12px 16px', fontSize:'13px', color:'#6B7280' }}>{fmtDate(inv.invoice_date)}</td>
                    <td style={{ padding:'12px 16px', fontSize:'13px', color: inv.status==='overdue'?'#DC2626':'#6B7280', fontWeight: inv.status==='overdue'?600:400 }}>{fmtDate(inv.due_date)}</td>
                    <td style={{ padding:'12px 16px', fontSize:'14px', fontWeight:700, color:'#1A3A2A' }}>{fmt(inv.total_amount)}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ background: statusBg[inv.status]||'#F3F4F6', color: statusColor[inv.status]||'#6B7280', padding:'3px 10px', borderRadius:'12px', fontSize:'12px', fontWeight:600, textTransform:'capitalize' }}>
                        {inv.status}
                      </span>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:'8px' }}>
                        <button onClick={() => openDetail(inv)} style={{ padding:'4px 12px', background:'#EFF6FF', color:'#2563EB', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:500 }}>View</button>
                        {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                          <button onClick={() => openSendModal(inv)} style={{ padding:'4px 12px', background:'#F0FDF4', color:'#16A34A', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:500 }}>Send</button>
                        )}
                        {inv.status === 'paid' && (
                          <button onClick={() => openCreditMemo(inv)} style={{ padding:'4px 12px', background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:500 }}>Credit</button>
                        )}
                        {inv.status === 'draft' && (
                          <button onClick={() => voidInvoice(inv)} style={{ padding:'4px 12px', background:'#F3F4F6', color:'#6B7280', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:500 }}>⊘ Void</button>
                        )}
                        {['sent','overdue'].includes(inv.status) && (
                          <button onClick={() => cancelInvoice(inv)} style={{ padding:'4px 12px', background:'#FFF7ED', color:'#EA580C', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:500 }}>Cancel</button>
                        )}
                        {inv.status === 'cancelled' && (
                          <button onClick={() => reactivateInvoice(inv)} style={{ padding:'4px 12px', background:'#F0F7FF', color:'#2563EB', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:500 }}>Reactivate</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* VOIDED & CANCELLED INVOICES SECTION */}
      {invoices.filter(i => i.status === 'void' || i.status === 'cancelled').length > 0 && (
        <div style={{ marginTop:'32px' }}>
          <div style={{ marginBottom:'16px' }}>
            <h2 style={{ margin:0, fontSize:'18px', fontWeight:'700', color:'#6B7280' }}>⊘ Voided & Cancelled Invoices</h2>
            <p style={{ margin:'4px 0 0', fontSize:'13px', color:'#9CA3AF' }}>Kept for audit purposes — these invoices cannot be modified</p>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'14px' }}>
              <thead>
                <tr style={{ background:'#F9FAFB', borderBottom:'2px solid #E5E7EB' }}>
                  {['Invoice #','Client','Amount','Date','Status','Reason','Notes','By'].map(h => (
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:700, color:'#6B7280', fontSize:'12px', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.filter(i => i.status === 'void' || i.status === 'cancelled').map(inv => (
                  <tr key={inv.id} onClick={() => openDetail(inv)} style={{ borderBottom:'1px solid #F3F4F6', opacity:0.7, cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'10px 14px', fontWeight:600, color:'#6B7280' }}>{inv.invoice_number}</td>
                    <td style={{ padding:'10px 14px', color:'#6B7280' }}>{inv.client_name}</td>
                    <td style={{ padding:'10px 14px', color:'#6B7280' }}>${parseFloat(inv.total_amount||0).toFixed(2)}</td>
                    <td style={{ padding:'10px 14px', color:'#9CA3AF', fontSize:'12px' }}>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-US') : '—'}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ padding:'2px 8px', borderRadius:'4px', fontSize:'11px', fontWeight:700,
                        background: inv.status === 'void' ? '#F3F4F6' : '#FFF7ED',
                        color: inv.status === 'void' ? '#6B7280' : '#EA580C' }}>
                        {inv.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px', color:'#6B7280', fontSize:'12px' }}>
                      {(inv.void_reason || inv.cancel_reason || '—').replace(/_/g,' ')}
                    </td>
                    <td style={{ padding:'10px 14px', color:'#9CA3AF', fontSize:'12px', maxWidth:'180px' }}>
                      {inv.void_notes || inv.cancel_notes || '—'}
                    </td>
                    <td style={{ padding:'10px 14px', color:'#9CA3AF', fontSize:'12px' }}>
                      {inv.voided_by_name || inv.cancelled_by_name || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CREDIT MEMOS SECTION */}
      {creditMemos.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <h2 style={{ margin:0, fontSize:'18px', fontWeight:'700', color:'#1A3A2A' }}>📋 Credit Memos</h2>
          </div>
          <div style={{ background:'white', borderRadius:'10px', border:'1px solid #E5E7EB', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
              <thead>
                <tr style={{ background:'#1A3A2A', color:'white' }}>
                  {['CM #','Date','Invoice Ref','Client','Reason','Amount','Status','Actions'].map(h => (
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:'600', fontSize:'12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {creditMemos.map((cm, i) => (
                  <tr key={cm.id} onClick={e => { if(!e.target.closest('button')) { setCmDetailModal(cm); setCmDetailTab('details'); setCmHistory([]); } }} style={{ borderBottom:'1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA', cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='#F0F7F4'} onMouseLeave={e=>e.currentTarget.style.background=i%2===0?'white':'#FAFAFA'}>
                    <td style={{ padding:'10px 14px', fontWeight:'700', color:'#DC2626' }}>{cm.cm_number}</td>
                    <td style={{ padding:'10px 14px', color:'#374151' }}>{new Date(cm.cm_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</td>
                    <td style={{ padding:'10px 14px', color:'#2563EB', fontWeight:'600' }}>{cm.invoice_number}</td>
                    <td style={{ padding:'10px 14px', color:'#374151' }}>{cm.client_name}</td>
                    <td style={{ padding:'10px 14px', color:'#374151' }}>{cm.reason_notes}</td>
                    <td style={{ padding:'10px 14px', fontWeight:'700', color:'#DC2626' }}>${parseFloat(cm.amount).toFixed(2)}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ padding:'3px 10px', borderRadius:'10px', fontSize:'11px', fontWeight:'600',
                        background: cm.status==='issued'?'#FEE2E2':cm.status==='refunded'?'#F0FDF4':cm.status==='applied'?'#EFF6FF':'#F3F4F6',
                        color: cm.status==='issued'?'#DC2626':cm.status==='refunded'?'#16A34A':cm.status==='applied'?'#2563EB':'#6B7280' }}>
                        {cm.status.charAt(0).toUpperCase() + cm.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      {cm.status === 'issued' && (
                        <div style={{ display:'flex', gap:'6px' }}>
                          {cm.disposition === 'refund' ? (
                            <button onClick={() => { setRefundCM(cm); setRefundForm({ refund_method:'check', refund_notes:'' }); setShowRefundModal(true); }}
                              style={{ padding:'3px 10px', background:'#F0FDF4', color:'#16A34A', border:'none', borderRadius:'6px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>
                              💵 Refund
                            </button>
                          ) : (
                            <span style={{ padding:'3px 10px', background:'#EFF6FF', color:'#2563EB',
                              borderRadius:'6px', fontSize:'11px', fontWeight:'600' }}>
                              📋 Pending next invoice
                            </span>
                          )}
                          <button onClick={() => voidCreditMemo(cm)}
                            style={{ padding:'3px 10px', background:'#F3F4F6', color:'#6B7280', border:'none', borderRadius:'6px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>
                            Void
                          </button>
                        </div>
                      )}
                      {cm.status === 'applied' && cm.applied_invoice_number && (
                        <span style={{ fontSize:'11px', color:'#6B7280' }}>→ {cm.applied_invoice_number}</span>
                      )}
                      {cm.status === 'refunded' && cm.refund_method && (
                        <span style={{ fontSize:'11px', color:'#6B7280' }}>{cm.refund_method}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* ── CREDIT MEMO DETAIL MODAL ── */}
      {cmDetailModal && (
        <div style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.55)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center' }}
          onClick={e => { if(e.target===e.currentTarget) setCmDetailModal(null); }}>
          <div style={{ background:'white',borderRadius:'14px',width:'95%',maxWidth:'620px',maxHeight:'90vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            {/* Header */}
            <div style={{ background:'linear-gradient(135deg,#7C1D1D,#DC2626)',padding:'20px 24px',borderRadius:'14px 14px 0 0',display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
              <div>
                <div style={{ color:'white',fontSize:'18px',fontWeight:700 }}>{cmDetailModal.cm_number}</div>
                <div style={{ color:'rgba(255,255,255,0.65)',fontSize:'13px',marginTop:'3px' }}>{cmDetailModal.client_name} &mdash; ref: {cmDetailModal.invoice_number}</div>
              </div>
              <div style={{ display:'flex',gap:'8px',alignItems:'center' }}>
                <span style={{ background:'rgba(255,255,255,0.2)',color:'white',padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:700,textTransform:'uppercase' }}>
                  {cmDetailModal.status}
                </span>
                <button onClick={() => setCmDetailModal(null)} style={{ background:'none',border:'none',color:'rgba(255,255,255,0.7)',fontSize:'22px',cursor:'pointer' }}>&times;</button>
              </div>
            </div>
            {/* Tabs */}
            <div style={{ display:'flex',borderBottom:'2px solid #E5E7EB',background:'#F9FAFB' }}>
              {[['details','📄 Details'],['history','🕐 History']].map(([key,label]) => (
                <button key={key} onClick={async () => {
                  setCmDetailTab(key);
                  if(key==='history' && cmHistory.length===0) {
                    setCmHistoryLoading(true);
                    try {
                      const r = await api.get(`/activity-log?entity_type=credit_memo&entity_id=${cmDetailModal.id}&limit=50`);
                      setCmHistory(r.data.data||[]);
                    } catch(e) { console.error(e); }
                    setCmHistoryLoading(false);
                  }
                }} style={{ padding:'12px 22px',border:'none',background:'none',cursor:'pointer',fontSize:'13px',fontWeight:700,
                  color: cmDetailTab===key ? '#DC2626' : '#6B7280',
                  borderBottom: cmDetailTab===key ? '2px solid #DC2626' : '2px solid transparent',
                  marginBottom:'-2px' }}>
                  {label}
                </button>
              ))}
            </div>

            {cmDetailTab==='history' ? (
              <div style={{ padding:'24px' }}>
                {cmHistoryLoading ? (
                  <div style={{ textAlign:'center',padding:'40px',color:'#9CA3AF' }}>Loading history...</div>
                ) : cmHistory.length===0 ? (
                  <div style={{ textAlign:'center',padding:'40px',color:'#9CA3AF' }}>
                    <div style={{ fontSize:'32px',marginBottom:'8px' }}>🕐</div>
                    <div style={{ fontWeight:600 }}>No activity recorded yet</div>
                  </div>
                ) : (
                  <div style={{ position:'relative' }}>
                    <div style={{ position:'absolute',left:'19px',top:'8px',bottom:'8px',width:'2px',background:'#E5E7EB' }} />
                    {cmHistory.map((log,i) => {
                      const actionColors = { created:'#DC2626',applied:'#2563EB',refunded:'#16A34A',voided:'#6B7280' };
                      const col = actionColors[log.action] || '#6B7280';
                      const dt = new Date(log.performed_at);
                      return (
                        <div key={i} style={{ display:'flex',gap:'14px',marginBottom:'20px',position:'relative' }}>
                          <div style={{ width:'38px',height:'38px',borderRadius:'50%',border:`2px solid ${col}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,zIndex:1,background:'white' }}>
                            <span style={{ fontSize:'14px' }}>
                              {log.action==='created'?'➕':log.action==='applied'?'💳':log.action==='refunded'?'💵':'🚫'}
                            </span>
                          </div>
                          <div style={{ flex:1,background:'#F9FAFB',borderRadius:'10px',padding:'12px 14px',border:'1px solid #E5E7EB' }}>
                            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'4px' }}>
                              <span style={{ fontSize:'13px',fontWeight:700,color:col,textTransform:'capitalize' }}>{log.action}</span>
                              <span style={{ fontSize:'11px',color:'#9CA3AF' }}>
                                {dt.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})} {dt.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}
                              </span>
                            </div>
                            {log.notes && <div style={{ fontSize:'12px',color:'#374151' }}>{log.notes}</div>}
                            <div style={{ fontSize:'11px',color:'#9CA3AF',marginTop:'6px' }}>by {log.performed_by_name||'System'}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding:'24px' }}>
                {/* Meta grid */}
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',marginBottom:'20px' }}>
                  {[
                    { label:'CM Date', value: cmDetailModal.cm_date ? new Date(cmDetailModal.cm_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—' },
                    { label:'Amount',  value: '$' + parseFloat(cmDetailModal.amount||0).toFixed(2) },
                    { label:'Disposition', value: (cmDetailModal.disposition||'—').replace(/_/g,' ') },
                  ].map((m,i) => (
                    <div key={i} style={{ background:'#F9FAFB',borderRadius:'8px',padding:'12px' }}>
                      <div style={{ fontSize:'11px',color:'#9CA3AF',fontWeight:600,textTransform:'uppercase',marginBottom:'4px' }}>{m.label}</div>
                      <div style={{ fontSize:'15px',fontWeight:700,color:'#7C1D1D' }}>{m.value}</div>
                    </div>
                  ))}
                </div>
                {/* Reason */}
                <div style={{ background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'10px',padding:'14px 16px',marginBottom:'20px' }}>
                  <div style={{ fontSize:'11px',color:'#9CA3AF',fontWeight:600,textTransform:'uppercase',marginBottom:'6px' }}>Reason</div>
                  <div style={{ fontSize:'13px',fontWeight:600,color:'#374151',marginBottom:'4px' }}>{(cmDetailModal.reason_type||'—').replace(/_/g,' ')}</div>
                  <div style={{ fontSize:'13px',color:'#6B7280' }}>{cmDetailModal.reason_notes||'—'}</div>
                </div>
                {/* Status info */}
                {cmDetailModal.status === 'applied' && cmDetailModal.applied_invoice_number && (
                  <div style={{ background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'#1E40AF' }}>
                    💳 Applied to invoice <strong>{cmDetailModal.applied_invoice_number}</strong>
                  </div>
                )}
                {cmDetailModal.status === 'refunded' && cmDetailModal.refund_method && (
                  <div style={{ background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'#15803D' }}>
                    💵 Refunded via <strong>{cmDetailModal.refund_method}</strong>{cmDetailModal.refund_notes ? ` — ${cmDetailModal.refund_notes}` : ''}
                  </div>
                )}
                {/* Actions — only for issued */}
                {cmDetailModal.status === 'issued' && (
                  <div style={{ display:'flex',gap:'10px',justifyContent:'flex-end',paddingTop:'12px',borderTop:'1px solid #E5E7EB' }}>
                    {cmDetailModal.disposition === 'refund' ? (
                      <button onClick={() => { setRefundCM(cmDetailModal); setRefundForm({ refund_method:'check', refund_notes:'' }); setShowRefundModal(true); setCmDetailModal(null); }}
                        style={{ padding:'9px 18px',background:'#16A34A',color:'white',border:'none',borderRadius:'7px',fontWeight:600,cursor:'pointer',fontSize:'13px' }}>
                        💵 Process Refund
                      </button>
                    ) : (
                      <span style={{ padding:'9px 18px',background:'#EFF6FF',color:'#2563EB',borderRadius:'7px',fontSize:'13px',fontWeight:600 }}>
                        📋 Pending next invoice
                      </span>
                    )}
                    <button onClick={() => { voidCreditMemo(cmDetailModal); setCmDetailModal(null); }}
                      style={{ padding:'9px 18px',background:'#F3F4F6',color:'#6B7280',border:'1px solid #D1D5DB',borderRadius:'7px',fontWeight:600,cursor:'pointer',fontSize:'13px' }}>
                      🚫 Void CM
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Invoice Modal */}
      {showModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'20px'}}>
          <div style={{background:'white',borderRadius:'12px',width:'700px',maxHeight:'90vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'20px 24px',borderBottom:'1px solid #E5E7EB'}}>
              <h3 style={{margin:0,fontSize:'18px',fontWeight:700,color:'#1A3A2A'}}>New Invoice</h3>
              <button onClick={() => setShowModal(false)} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'#6B7280'}}>✕</button>
            </div>
            <div style={{padding:'24px'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'16px'}}>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Client *</label>
                  <div style={{position:'relative'}}>
                    <input
                      type="text"
                      placeholder="Type 2+ letters to search client..."
                      value={clientSearch}
                      onChange={e => {
                        setClientSearch(e.target.value);
                        setClientDropdownOpen(e.target.value.length >= 2);
                        if (!e.target.value) { onClientChange(''); }
                      }}
                      onFocus={() => { if (clientSearch.length >= 2) setClientDropdownOpen(true); }}
                      onBlur={() => setTimeout(() => setClientDropdownOpen(false), 200)}
                      style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}}
                    />
                    {clientDropdownOpen && (
                      <div style={{position:'absolute',top:'100%',left:0,right:0,background:'white',border:'1px solid #D1D5DB',borderRadius:'8px',boxShadow:'0 4px 12px rgba(0,0,0,0.15)',zIndex:100,maxHeight:'200px',overflowY:'auto',marginTop:'2px'}}>
                        {clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())).length === 0
                          ? <div style={{padding:'10px 14px',fontSize:'13px',color:'#9CA3AF'}}>No clients found</div>
                          : clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())).map(c => (
                            <div key={c.id}
                              onMouseDown={() => {
                                setClientSearch(c.name);
                                setClientDropdownOpen(false);
                                onClientChange(String(c.id));
                              }}
                              style={{padding:'10px 14px',fontSize:'14px',cursor:'pointer',borderBottom:'1px solid #F3F4F6',color:'#1A3A2A',fontWeight: String(c.id) === String(form.client_id) ? 700 : 400}}
                              onMouseEnter={e => e.currentTarget.style.background='#F0FDF4'}
                              onMouseLeave={e => e.currentTarget.style.background='white'}
                            >{c.name}</div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                  {/* Credit available alert */}
                  {clientCredits.total > 0 && (
                    <div style={{ marginTop:'10px', padding:'12px 14px', background:'#FFFBEB',
                      border:'1px solid #D97706', borderRadius:'8px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <p style={{ margin:0, fontSize:'13px', fontWeight:'700', color:'#92400E' }}>
                            ⚠️ This client has ${parseFloat(clientCredits.total).toFixed(2)} in available credits
                          </p>
                          <p style={{ margin:'2px 0 0', fontSize:'12px', color:'#78350F' }}>
                            {clientCredits.data.length} credit memo{clientCredits.data.length !== 1 ? 's' : ''}: {clientCredits.data.map(cm => cm.cm_number).join(', ')}
                          </p>
                        </div>
                        <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer' }}>
                          <input type="checkbox" checked={applyCredit}
                            onChange={e => setApplyCredit(e.target.checked)}
                            style={{ width:'16px', height:'16px', cursor:'pointer' }} />
                          <span style={{ fontSize:'13px', fontWeight:'600', color:'#92400E' }}>Apply to this invoice</span>
                        </label>
                      </div>
                      {applyCredit && (
                        <div style={{ marginTop:'8px', padding:'8px 12px', background:'#FEF3C7',
                          borderRadius:'6px', fontSize:'12px', color:'#78350F' }}>
                          💡 Credit of <strong>${parseFloat(clientCredits.total).toFixed(2)}</strong> will be applied
                          after saving. New total will be reduced accordingly.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Service Agreement</label>
                  <select value={form.service_agreement_id} onChange={e => setForm(f=>({...f,service_agreement_id:e.target.value}))}
                    style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}}>
                    <option value="">— None —</option>
                    {agreements.filter(a => !form.client_id || String(a.client_id) === String(form.client_id)).map(a =>
                      <option key={a.id} value={a.id}>SP-{String(a.id).padStart(4,'0')} — {a.service_type}</option>)}
                  </select>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'20px'}}>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Invoice Date *</label>
                  <input type="date" value={form.invoice_date} onChange={e => {
                    const newDate = e.target.value;
                    const ag = agreements.find(a => String(a.id) === String(form.service_agreement_id));
                    const newDue = ag ? calcDueDate(newDate, ag.billing_terms) : calcDueDate(newDate, 'net_30');
                    setForm(f => ({ ...f, invoice_date: newDate, due_date: newDue }));
                  }}
                    style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}} />
                </div>
                <div>
                  <label style={{display:'block',fontSize:'13px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>
                    Due Date
                    {form.service_agreement_id && agreements.find(a => String(a.id) === String(form.service_agreement_id))?.billing_terms && (
                      <span style={{marginLeft:'8px',fontSize:'11px',fontWeight:'400',color:'#6B7280',background:'#F3F4F6',padding:'2px 8px',borderRadius:'10px'}}>
                        {agreements.find(a => String(a.id) === String(form.service_agreement_id))?.billing_terms?.replace(/_/g,' ')}
                      </span>
                    )}
                  </label>
                  <input type="date" value={form.due_date} onChange={e => setForm(f=>({...f,due_date:e.target.value}))}
                    style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box'}} />
                </div>
              </div>
              <div style={{marginBottom:'16px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                  <label style={{fontSize:'13px',fontWeight:600,color:'#374151'}}>Line Items</label>
                  <button onClick={addItem} style={{background:'#F0FDF4',color:'#16A34A',border:'1px solid #BBF7D0',borderRadius:'6px',padding:'4px 12px',fontSize:'12px',cursor:'pointer',fontWeight:600}}>+ Add Line</button>
                </div>
                {billableProducts.length > 0 && (
                  <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'8px',padding:'12px',marginBottom:'12px'}}>
                    <p style={{margin:'0 0 8px',fontSize:'12px',fontWeight:600,color:'#15803D'}}>Quick Add Billable Products:</p>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
                      {billableProducts.map(p => (
                        <button key={p.id} onClick={() => addBillableProduct(p)}
                          style={{background:'white',border:'1px solid #BBF7D0',borderRadius:'6px',padding:'4px 10px',fontSize:'12px',cursor:'pointer',color:'#15803D'}}>
                          + {p.name} (${parseFloat(p.unit_price).toFixed(2)})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {form.items.length > 0 ? (
                  <div style={{border:'1px solid #E5E7EB',borderRadius:'8px',overflow:'hidden'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead>
                        <tr style={{background:'#F9FAFB'}}>
                          <th style={{padding:'8px 12px',textAlign:'left',fontSize:'12px',color:'#6B7280',fontWeight:600}}>Description</th>
                          <th style={{padding:'8px 12px',textAlign:'center',fontSize:'12px',color:'#6B7280',fontWeight:600,width:'70px'}}>Qty</th>
                          <th style={{padding:'8px 12px',textAlign:'right',fontSize:'12px',color:'#6B7280',fontWeight:600,width:'100px'}}>Unit Price</th>
                          <th style={{padding:'8px 12px',textAlign:'left',fontSize:'12px',color:'#6B7280',fontWeight:600,width:'130px'}}>Tax</th>
                          <th style={{padding:'8px 12px',textAlign:'right',fontSize:'12px',color:'#6B7280',fontWeight:600,width:'90px'}}>Total</th>
                          <th style={{width:'40px'}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((it, idx) => (
                          <tr key={idx} style={{borderTop:'1px solid #F3F4F6'}}>
                            <td style={{padding:'8px 12px'}}>
                              <input value={it.description} onChange={e => updateItem(idx,'description',e.target.value)}
                                style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:'6px',padding:'6px 8px',fontSize:'13px',boxSizing:'border-box'}} />
                            </td>
                            <td style={{padding:'8px 12px'}}>
                              <input type="number" min="0" step="0.01" value={it.quantity} onChange={e => updateItem(idx,'quantity',e.target.value)}
                                style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:'6px',padding:'6px 8px',fontSize:'13px',textAlign:'center',boxSizing:'border-box'}} />
                            </td>
                            <td style={{padding:'8px 12px'}}>
                              <input type="number" min="0" step="0.01" value={it.unit_price} onChange={e => updateItem(idx,'unit_price',e.target.value)}
                                style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:'6px',padding:'6px 8px',fontSize:'13px',textAlign:'right',boxSizing:'border-box'}} />
                            </td>
                            <td style={{padding:'8px 12px'}}>
                              <select value={it.tax_category_id||''} onChange={e => updateItem(idx,'tax_category_id', e.target.value ? parseInt(e.target.value) : null)}
                                style={{width:'100%',border: it.tax_category_id ? '1px solid #BBF7D0' : '1px solid #FCD34D',borderRadius:'6px',padding:'5px 6px',fontSize:'11px',boxSizing:'border-box',color: it.tax_category_id ? '#374151' : '#B45309',background: it.tax_category_id ? 'white' : '#FFFBEB'}}>
                                <option value="">⚠ Select tax...</option>
                                {taxCategories.map(tc => (
                                  <option key={tc.id} value={tc.id}>
                                    {tc.name}{tc.is_exempt ? ' (Exempt)' : tc.is_zero_rated ? ' (0%)' : ` (${tc.rate}%)`}
                                  </option>
                                ))}
                              </select>
                              {getLineTax(it) > 0 && <div style={{fontSize:'10px',color:'#B45309',fontWeight:600,marginTop:'2px',textAlign:'right'}}>+{fmt(getLineTax(it))} tax</div>}
                            </td>
                            <td style={{padding:'8px 12px',textAlign:'right',fontWeight:600,fontSize:'13px',color:'#1A3A2A'}}>
                              {fmt(parseFloat(it.quantity||0)*parseFloat(it.unit_price||0) + getLineTax(it))}
                            </td>
                            <td style={{padding:'8px 4px',textAlign:'center'}}>
                              <button onClick={() => removeItem(idx)} style={{background:'none',border:'none',color:'#DC2626',cursor:'pointer',fontSize:'16px',padding:'2px 6px'}}>×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{padding:'10px 12px',background:'#F9FAFB',borderTop:'2px solid #1A3A2A',textAlign:'right'}}>
                      <span style={{fontSize:'16px',fontWeight:700,color:'#1A3A2A'}}>TOTAL: {fmt(getTotal())}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{padding:'24px',textAlign:'center',border:'2px dashed #E5E7EB',borderRadius:'8px',color:'#9CA3AF',fontSize:'14px'}}>
                    No items yet — select a client with a service agreement or click "+ Add Line"
                  </div>
                )}
              </div>
              <div style={{marginBottom:'20px'}}>
                <label style={{display:'block',fontSize:'13px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} rows={2}
                  placeholder="Payment instructions, thank you note, etc."
                  style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:'8px',fontSize:'14px',boxSizing:'border-box',resize:'vertical'}} />
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:'10px',paddingTop:'16px',borderTop:'1px solid #E5E7EB'}}>
                <button onClick={() => setShowModal(false)} style={{padding:'10px 20px',border:'1px solid #D1D5DB',borderRadius:'8px',background:'white',cursor:'pointer',fontSize:'14px',color:'#374151'}}>Cancel</button>
                <button onClick={() => handleSave(false)} disabled={saving}
                  style={{padding:'10px 20px',border:'none',borderRadius:'8px',background:'#6B7280',color:'white',cursor:'pointer',fontSize:'14px',fontWeight:600}}>
                  {saving ? 'Saving...' : 'Save as Draft'}
                </button>
                <button onClick={() => handleSave(true)} disabled={saving}
                  style={{padding:'10px 20px',border:'none',borderRadius:'8px',background:'#1A3A2A',color:'white',cursor:'pointer',fontSize:'14px',fontWeight:600}}>
                  {saving ? 'Sending...' : 'Save & Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── INVOICE DETAIL MODAL ── */}
      {showDetailModal && (
        <div style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.55)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center' }}
          onClick={e => { if(e.target===e.currentTarget) setShowDetailModal(false); }}>
          <div style={{ background:'white',borderRadius:'14px',width:'95%',maxWidth:'680px',maxHeight:'92vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            {detailLoading ? (
              <div style={{ padding:'60px',textAlign:'center',color:'#9CA3AF' }}>Loading...</div>
            ) : selectedInvoice && (
              <>
                {/* Header */}
                <div style={{ background:'linear-gradient(135deg,#1A3A2A,#2D5C3F)',padding:'20px 24px',borderRadius:'14px 14px 0 0',display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
                  <div>
                    <div style={{ color:'white',fontSize:'18px',fontWeight:700 }}>{selectedInvoice.invoice_number}</div>
                    <div style={{ color:'rgba(255,255,255,0.65)',fontSize:'13px',marginTop:'3px' }}>{selectedInvoice.client_name} &mdash; {selectedInvoice.facility_name || ''}</div>
                  </div>
                  <div style={{ display:'flex',gap:'8px',alignItems:'center' }}>
                    <span style={{ background: selectedInvoice.status==='paid'?'#D1FAE5':selectedInvoice.status==='sent'?'#DBEAFE':'#F3F4F6', color: selectedInvoice.status==='paid'?'#065F46':selectedInvoice.status==='sent'?'#1E40AF':'#374151', padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:700,textTransform:'uppercase' }}>
                      {selectedInvoice.status}
                    </span>
                    <button onClick={() => setShowDetailModal(false)} style={{ background:'none',border:'none',color:'rgba(255,255,255,0.7)',fontSize:'22px',cursor:'pointer' }}>&times;</button>
                  </div>
                </div>

                {/* Tabs */}
                <div style={{ display:'flex',borderBottom:'2px solid #E5E7EB',background:'#F9FAFB' }}>
                  {[['details','📄 Details'],['history','🕐 History']].map(([key,label]) => (
                    <button key={key} onClick={async () => {
                      setInvoiceDetailTab(key);
                      if(key==='history' && invoiceHistory.length===0) {
                        setHistoryLoading(true);
                        try {
                          const r = await api.get(`/activity-log?entity_type=invoice&entity_id=${selectedInvoice.id}&limit=50`);
                          setInvoiceHistory(r.data.data||[]);
                        } catch(e) { console.error(e); }
                        setHistoryLoading(false);
                      }
                    }} style={{ padding:'12px 22px',border:'none',background:'none',cursor:'pointer',fontSize:'13px',fontWeight:700,
                      color: invoiceDetailTab===key ? '#2D5C3F' : '#6B7280',
                      borderBottom: invoiceDetailTab===key ? '2px solid #2D5C3F' : '2px solid transparent',
                      marginBottom:'-2px' }}>
                      {label}
                    </button>
                  ))}
                </div>

                {invoiceDetailTab==='history' ? (
                  <div style={{ padding:'24px' }}>
                    {historyLoading ? (
                      <div style={{ textAlign:'center',padding:'40px',color:'#9CA3AF' }}>Loading history...</div>
                    ) : invoiceHistory.length===0 ? (
                      <div style={{ textAlign:'center',padding:'40px',color:'#9CA3AF' }}>
                        <div style={{ fontSize:'32px',marginBottom:'8px' }}>🕐</div>
                        <div style={{ fontWeight:600 }}>No activity recorded yet</div>
                      </div>
                    ) : (
                      <div style={{ position:'relative' }}>
                        <div style={{ position:'absolute',left:'19px',top:'8px',bottom:'8px',width:'2px',background:'#E5E7EB' }} />
                        {invoiceHistory.map((log,i) => {
                          const actionColors = {
                            created:'#16A34A', updated:'#2563EB', sent:'#7C3AED',
                            paid:'#059669', voided:'#DC2626', cancelled:'#D97706',
                            credit_applied:'#0891B2', refunded:'#EA580C'
                          };
                          const col = actionColors[log.action] || '#6B7280';
                          const dt = new Date(log.performed_at);
                          return (
                            <div key={i} style={{ display:'flex',gap:'14px',marginBottom:'20px',position:'relative' }}>
                              <div style={{ width:'38px',height:'38px',borderRadius:'50%',background:col+'18',border:`2px solid ${col}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,zIndex:1,background:'white' }}>
                                <span style={{ fontSize:'14px' }}>
                                  {log.action==='created'?'➕':log.action==='updated'?'✏️':log.action==='sent'?'✉️':log.action==='paid'?'✅':log.action==='voided'?'🚫':log.action==='cancelled'?'⚠️':log.action==='credit_applied'?'💳':'🔄'}
                                </span>
                              </div>
                              <div style={{ flex:1,background:'#F9FAFB',borderRadius:'10px',padding:'12px 14px',border:'1px solid #E5E7EB' }}>
                                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'4px' }}>
                                  <span style={{ fontSize:'13px',fontWeight:700,color:col,textTransform:'capitalize' }}>{log.action.replace(/_/g,' ')}</span>
                                  <span style={{ fontSize:'11px',color:'#9CA3AF' }}>
                                    {dt.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})} {dt.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}
                                  </span>
                                </div>
                                {log.notes && <div style={{ fontSize:'12px',color:'#374151',marginBottom:'4px' }}>{log.notes}</div>}
                                {log.changed_fields && Object.keys(log.changed_fields).length>0 && (
                                  <div style={{ marginTop:'6px' }}>
                                    {Object.entries(log.changed_fields).map(([field,val],j) => (
                                      <div key={j} style={{ fontSize:'11px',color:'#6B7280',background:'white',border:'1px solid #E5E7EB',borderRadius:'4px',padding:'3px 8px',display:'inline-block',marginRight:'4px',marginBottom:'3px' }}>
                                        <span style={{ fontWeight:600 }}>{field.replace(/_/g,' ')}: </span>
                                        {typeof val==='object' && val!==null && val.from!==undefined
                                          ? <span><span style={{color:'#DC2626'}}>{String(val.from||'—')}</span> → <span style={{color:'#16A34A'}}>{String(val.to||'—')}</span></span>
                                          : <span>{String(val)}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div style={{ fontSize:'11px',color:'#9CA3AF',marginTop:'6px' }}>
                                  by {log.performed_by_name||'System'}
                                  {log.source && log.source!=='admin' && <span style={{ marginLeft:'6px',background:'#EEF2FF',color:'#4338CA',padding:'1px 6px',borderRadius:'10px',fontSize:'10px' }}>{log.source}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                <div style={{ padding:'24px' }}>
                  {/* Meta info */}
                  <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',marginBottom:'20px' }}>
                    {[
                      { label:'Invoice Date', value: selectedInvoice.invoice_date ? new Date(selectedInvoice.invoice_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—' },
                      { label:'Due Date',     value: selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—' },
                      { label:'Total Amount', value: fmt(selectedInvoice.total_amount) },
                    ].map((m,i) => (
                      <div key={i} style={{ background:'#F9FAFB',borderRadius:'8px',padding:'12px' }}>
                        <div style={{ fontSize:'11px',color:'#9CA3AF',fontWeight:600,textTransform:'uppercase',marginBottom:'4px' }}>{m.label}</div>
                        <div style={{ fontSize:'15px',fontWeight:700,color:'#1A3A2A' }}>{m.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Void / Cancel reason */}
                  {(selectedInvoice.status === 'void' || selectedInvoice.status === 'cancelled') && (
                    <div style={{ background: selectedInvoice.status==='void'?'#F3F4F6':'#FFF7ED',
                      border: `1px solid ${selectedInvoice.status==='void'?'#D1D5DB':'#FED7AA'}`,
                      borderRadius:'10px', padding:'14px 16px', marginBottom:'20px' }}>
                      <div style={{ fontWeight:700, fontSize:'13px',
                        color: selectedInvoice.status==='void'?'#374151':'#EA580C', marginBottom:'8px' }}>
                        {selectedInvoice.status==='void' ? '🚫 Voided Invoice' : '⚠️ Cancelled Invoice'}
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                        <div>
                          <div style={{ fontSize:'11px', color:'#9CA3AF', fontWeight:600, textTransform:'uppercase', marginBottom:'3px' }}>Reason</div>
                          <div style={{ fontSize:'13px', color:'#374151' }}>
                            {(selectedInvoice.void_reason || selectedInvoice.cancel_reason || '—').replace(/_/g,' ')}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize:'11px', color:'#9CA3AF', fontWeight:600, textTransform:'uppercase', marginBottom:'3px' }}>Notes</div>
                          <div style={{ fontSize:'13px', color:'#374151' }}>{selectedInvoice.void_notes || selectedInvoice.cancel_notes || '—'}</div>
                        </div>
                      </div>
                      {(selectedInvoice.voided_by_name || selectedInvoice.cancelled_by_name) && (
                        <div style={{ fontSize:'11px', color:'#9CA3AF', marginTop:'8px' }}>
                          by {selectedInvoice.voided_by_name || selectedInvoice.cancelled_by_name}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Line items */}
                  <div style={{ border:'1px solid #E5E7EB',borderRadius:'8px',overflow:'hidden',marginBottom:'20px' }}>
                    <table style={{ width:'100%',borderCollapse:'collapse' }}>
                      <thead>
                        <tr style={{ background:'#1A3A2A' }}>
                          {['Description','Qty','Unit Price','Total'].map(h => (
                            <th key={h} style={{ padding:'10px 14px',textAlign: h==='Description'?'left':'right',fontSize:'12px',color:'white',fontWeight:600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedInvoice.items||[]).map((it,i) => (
                          <tr key={i} style={{ borderBottom:'1px solid #F3F4F6' }}>
                            <td style={{ padding:'10px 14px',fontSize:'13px' }}>{it.description}</td>
                            <td style={{ padding:'10px 14px',fontSize:'13px',textAlign:'right' }}>{it.quantity}</td>
                            <td style={{ padding:'10px 14px',fontSize:'13px',textAlign:'right' }}>{fmt(it.unit_price)}</td>
                            <td style={{ padding:'10px 14px',fontSize:'13px',textAlign:'right',fontWeight:600 }}>{fmt(it.total_price)}</td>
                          </tr>
                        ))}
                        <tr style={{ background:'#F0F7F4',borderTop:'2px solid #1A3A2A' }}>
                          <td colSpan={3} style={{ padding:'12px 14px',textAlign:'right',fontWeight:700,fontSize:'15px' }}>TOTAL</td>
                          <td style={{ padding:'12px 14px',textAlign:'right',fontWeight:700,fontSize:'16px',color:'#1A3A2A' }}>{fmt(selectedInvoice.total_amount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Mark Paid panel */}
                  {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && selectedInvoice.status !== 'void' && (
                    <div style={{ background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'10px',padding:'16px',marginBottom:'16px' }}>
                      <div style={{ fontSize:'13px',fontWeight:700,color:'#15803D',marginBottom:'10px' }}>&#9745; Mark as Paid</div>
                      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'10px' }}>
                        <div>
                          <label style={{ display:'block',fontSize:'11px',fontWeight:600,color:'#374151',marginBottom:'4px',textTransform:'uppercase' }}>Payment Method</label>
                          <select value={payForm.payment_method} onChange={e => setPayForm(f=>({...f,payment_method:e.target.value}))}
                            style={{ width:'100%',padding:'8px 10px',border:'1px solid #D1D5DB',borderRadius:'6px',fontSize:'13px' }}>
                            {['check','cash','ach','credit_card','zelle','other'].map(m => (
                              <option key={m} value={m}>{m.replace('_',' ').replace(/\w/g,c=>c.toUpperCase())}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ display:'block',fontSize:'11px',fontWeight:600,color:'#374151',marginBottom:'4px',textTransform:'uppercase' }}>Amount Paid</label>
                          <input type="number" step="0.01" value={payForm.paid_amount} onChange={e => setPayForm(f=>({...f,paid_amount:e.target.value}))}
                            style={{ width:'100%',padding:'8px 10px',border:'1px solid #D1D5DB',borderRadius:'6px',fontSize:'13px',boxSizing:'border-box' }} />
                        </div>
                      </div>
                      <button onClick={() => markPaid(selectedInvoice.id)} disabled={markingPaid}
                        style={{ width:'100%',padding:'10px',background: markingPaid?'#9CA3AF':'#16A34A',color:'white',border:'none',borderRadius:'7px',fontWeight:700,cursor: markingPaid?'not-allowed':'pointer',fontSize:'14px' }}>
                        {markingPaid ? 'Processing...' : '✓ Confirm Payment'}
                      </button>
                    </div>
                  )}

                  {/* Payments history */}
                  {(selectedInvoice.payments||[]).length > 0 && (
                    <div style={{ marginBottom:'16px' }}>
                      <div style={{ fontSize:'12px',fontWeight:700,color:'#374151',marginBottom:'8px',textTransform:'uppercase' }}>Payment History</div>
                      {selectedInvoice.payments.map((p,i) => (
                        <div key={i} style={{ display:'flex',justifyContent:'space-between',padding:'8px 12px',background:'#F0FDF4',borderRadius:'6px',marginBottom:'4px',fontSize:'13px' }}>
                          <span>{new Date(p.payment_date||p.created_at).toLocaleDateString()} &mdash; {(p.payment_method||'').replace('_',' ')}</span>
                          <strong style={{ color:'#16A34A' }}>{fmt(p.amount)}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Footer actions */}
                  <div style={{ display:'flex',gap:'10px',justifyContent:'flex-end',paddingTop:'12px',borderTop:'1px solid #E5E7EB' }}>
                    <button onClick={() => downloadInvoicePdf(selectedInvoice)}
                      style={{ padding:'9px 18px',background:'#F3F4F6',color:'#374151',border:'1px solid #D1D5DB',borderRadius:'7px',fontWeight:600,cursor:'pointer',fontSize:'13px' }}>
                      📄 View PDF
                    </button>
                    {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && selectedInvoice.status !== 'void' && (
                      <button onClick={() => openSendModal(selectedInvoice)} disabled={sending}
                        style={{ padding:'9px 18px',background: sending?'#9CA3AF':'#2563EB',color:'white',border:'none',borderRadius:'7px',fontWeight:600,cursor: sending?'not-allowed':'pointer',fontSize:'13px' }}>
                        {sending ? 'Sending...' : '✉ Send to Client'}
                      </button>
                    )}
                  </div>
                </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── MONTHLY BILLING MODAL ── */}
      {showBillingModal && (
        <div style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.55)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center' }}>
          <div style={{ background:'white',borderRadius:'14px',width:'95%',maxWidth:'620px',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ background:'linear-gradient(135deg,#1565C0,#0D47A1)',padding:'20px 24px',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div>
                <div style={{ color:'white',fontSize:'16px',fontWeight:700 }}>&#9889; Monthly Billing Engine</div>
                <div style={{ color:'rgba(255,255,255,0.65)',fontSize:'12px',marginTop:'3px' }}>Generate invoices for all active recurring services</div>
              </div>
              <button onClick={() => setShowBillingModal(false)} style={{ background:'none',border:'none',color:'rgba(255,255,255,0.7)',fontSize:'22px',cursor:'pointer' }}>&times;</button>
            </div>
            <div style={{ padding:'24px' }}>
              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block',fontSize:'12px',fontWeight:700,color:'#374151',marginBottom:'6px',textTransform:'uppercase' }}>Billing Month</label>
                <input type="month" value={billingMonth}
                  onChange={e => { setBillingMonth(e.target.value); setBillingPreview(null); setBillingConfirmed(false); }}
                  style={{ padding:'9px 12px',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'15px',width:'200px' }} />
              </div>

              {!billingPreview && !billingConfirmed && (
                <button onClick={runBillingPreview} disabled={billingRunning}
                  style={{ background: billingRunning ? '#9CA3AF' : '#1565C0',color:'white',border:'none',borderRadius:'8px',padding:'10px 24px',fontSize:'14px',fontWeight:700,cursor: billingRunning ? 'not-allowed' : 'pointer' }}>
                  {billingRunning ? 'Calculating...' : '&#128269; Preview Billing'}
                </button>
              )}

              {billingPreview && !billingConfirmed && (
                <div>
                  <div style={{ background:'#EFF6FF',borderRadius:'10px',padding:'16px',marginBottom:'16px',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px' }}>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize:'28px',fontWeight:700,color:'#1565C0' }}>{billingPreview.to_create}</div>
                      <div style={{ fontSize:'12px',color:'#6B7280',marginTop:'2px' }}>To Generate</div>
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize:'28px',fontWeight:700,color:'#16A34A' }}>${parseFloat(billingPreview.total_amount||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div>
                      <div style={{ fontSize:'12px',color:'#6B7280',marginTop:'2px' }}>Total Amount</div>
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize:'28px',fontWeight:700,color:'#9CA3AF' }}>{billingPreview.to_skip}</div>
                      <div style={{ fontSize:'12px',color:'#6B7280',marginTop:'2px' }}>Already Billed</div>
                    </div>
                  </div>
                  <div style={{ maxHeight:'220px',overflowY:'auto',marginBottom:'16px' }}>
                    {(billingPreview.results||[]).map((r,i) => (
                      <div key={i} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',background: r.skipped ? '#F9FAFB' : '#F0FDF4',borderRadius:'8px',marginBottom:'6px',border:'1px solid ' + (r.skipped ? '#E5E7EB' : '#BBF7D0') }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:'13px',fontWeight:600,color: r.skipped ? '#9CA3AF' : '#1A3A2A' }}>{r.client_name}</div>
                          <div style={{ fontSize:'11px',color:'#6B7280',marginTop:'2px' }}>{r.skipped ? r.reason : r.line_description}</div>
                          {!r.skipped && r.available_credit > 0 && (
                            <div style={{ display:'inline-flex',alignItems:'center',gap:'4px',marginTop:'4px',background:'#F5F3FF',border:'1px solid #DDD6FE',borderRadius:'4px',padding:'2px 7px' }}>
                              <span style={{ fontSize:'11px',color:'#7C3AED',fontWeight:700 }}>💳 ${parseFloat(r.available_credit).toFixed(2)} credit will be applied</span>
                            </div>
                          )}
                        </div>
                        {!r.skipped && (
                          <div style={{ textAlign:'right',minWidth:'80px' }}>
                            <div style={{ fontSize:'14px',fontWeight:700,color:'#16A34A' }}>${parseFloat(r.amount||0).toFixed(2)}</div>
                            {r.available_credit > 0 && (
                              <div style={{ fontSize:'11px',color:'#7C3AED',fontWeight:600 }}>
                                → ${Math.max(0, parseFloat(r.amount||0) - parseFloat(r.available_credit||0)).toFixed(2)} net
                              </div>
                            )}
                          </div>
                        )}
                        {r.skipped && <span style={{ fontSize:'11px',color:'#9CA3AF',fontWeight:600 }}>SKIP</span>}
                      </div>
                    ))}
                  </div>
                  {billingPreview.to_create > 0 ? (
                    <div style={{ display:'flex',gap:'10px' }}>
                      <button onClick={() => { setBillingPreview(null); setBillingConfirmed(false); }}
                        style={{ flex:1,padding:'10px',border:'1px solid #D1D5DB',borderRadius:'7px',background:'white',color:'#6B7280',cursor:'pointer',fontWeight:600 }}>
                        &#8592; Re-select Month
                      </button>
                      <button onClick={executeBilling} disabled={billingRunning}
                        style={{ flex:2,padding:'10px',border:'none',borderRadius:'7px',background: billingRunning ? '#9CA3AF' : '#16A34A',color:'white',cursor: billingRunning ? 'not-allowed' : 'pointer',fontWeight:700,fontSize:'14px' }}>
                        {billingRunning ? 'Generating...' : '✓ Generate ' + billingPreview.to_create + ' Invoice(s)'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ textAlign:'center',padding:'16px',color:'#6B7280',background:'#F9FAFB',borderRadius:'8px' }}>
                      All clients already billed for this month.
                    </div>
                  )}
                </div>
              )}

              {billingConfirmed && billingPreview && (
                <div style={{ textAlign:'center',padding:'24px' }}>
                  <div style={{ fontSize:'48px',marginBottom:'12px' }}>&#9989;</div>
                  <div style={{ fontSize:'18px',fontWeight:700,color:'#16A34A',marginBottom:'6px' }}>
                    {billingPreview.created_count} Invoice(s) Created!
                  </div>
                  <div style={{ fontSize:'13px',color:'#6B7280',marginBottom:'20px' }}>
                    {billingPreview.month_label} billing complete — ${parseFloat(billingPreview.total_amount||0).toLocaleString('en-US',{minimumFractionDigits:2})} total
                  </div>
                  <button onClick={() => setShowBillingModal(false)}
                    style={{ padding:'10px 28px',background:'#1A3A2A',color:'white',border:'none',borderRadius:'8px',fontWeight:700,cursor:'pointer',fontSize:'14px' }}>
                    View Invoices
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor={confirmDialog.confirmColor || '#DC2626'}
          icon={confirmDialog.icon || '⚠'}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {/* SEND MODAL */}
      {sendModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000,padding:'20px'}}>
          <div style={{background:'white',borderRadius:'12px',width:'480px',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3 style={{margin:0,fontSize:'17px',fontWeight:700,color:'#1A3A2A'}}>📧 Send Invoice {sendModal.invoice_number}</h3>
              <button onClick={() => setSendModal(null)} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'#6B7280'}}>✕</button>
            </div>
            <div style={{padding:'20px 24px'}}>
              <p style={{margin:'0 0 14px',fontSize:'13px',color:'#6B7280'}}>Select recipients for this invoice:</p>
              {sendRecipients.length === 0
                ? <p style={{fontSize:'13px',color:'#DC2626',marginBottom:'14px'}}>⚠ No billing email configured for this client. Add one in the Client settings.</p>
                : sendRecipients.map((r,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:'#F9FAFB',borderRadius:'6px',marginBottom:'6px',border:'1px solid #E5E7EB'}}>
                    <input type="checkbox" checked={r.checked}
                      onChange={() => setSendRecipients(prev => prev.map((x,j) => j===i ? {...x,checked:!x.checked} : x))}
                      style={{width:'16px',height:'16px',cursor:'pointer'}} />
                    <div style={{flex:1}}>
                      <div style={{fontSize:'13px',fontWeight:600,color:'#1A3A2A'}}>{r.email}</div>
                      <div style={{fontSize:'11px',color:'#9CA3AF'}}>{r.label}</div>
                    </div>
                  </div>
                ))
              }
              <div style={{marginTop:'14px',marginBottom:'20px'}}>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Additional recipient (optional)</label>
                <input type="email" value={sendExtraEmail}
                  onChange={e => setSendExtraEmail(e.target.value)}
                  placeholder="other@example.com"
                  style={{width:'100%',padding:'9px 12px',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={submitSendInvoice} disabled={sendingModal}
                  style={{flex:1,padding:'10px',background:'#16A34A',color:'white',border:'none',borderRadius:'7px',fontSize:'14px',fontWeight:700,cursor:'pointer'}}>
                  {sendingModal ? 'Sending...' : '📧 Send Invoice'}
                </button>
                <button onClick={() => setSendModal(null)}
                  style={{flex:1,padding:'10px',background:'white',color:'#6B7280',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'14px',cursor:'pointer'}}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VOID MODAL */}
      {voidModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000,padding:'20px'}}>
          <div style={{background:'white',borderRadius:'12px',width:'480px',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3 style={{margin:0,fontSize:'17px',fontWeight:700,color:'#1A3A2A'}}>⊘ Void Invoice {voidModal.invoice_number}</h3>
              <button onClick={() => setVoidModal(null)} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'#6B7280'}}>✕</button>
            </div>
            <div style={{padding:'20px 24px'}}>
              <p style={{margin:'0 0 16px',fontSize:'13px',color:'#6B7280'}}>This invoice will be marked as VOID and kept for audit purposes. This action cannot be undone.</p>
              <div style={{marginBottom:'14px'}}>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Reason *</label>
                <select value={voidForm.reason} onChange={e => setVoidForm(f=>({...f,reason:e.target.value}))}
                  style={{width:'100%',padding:'9px 12px',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'14px'}}>
                  <option value="">— Select reason —</option>
                  <option value="data_entry_error">Data Entry Error</option>
                  <option value="duplicate">Duplicate Invoice</option>
                  <option value="wrong_client">Wrong Client</option>
                  <option value="wrong_amount">Wrong Amount</option>
                  <option value="service_cancelled">Service Cancelled</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{marginBottom:'20px'}}>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Notes (optional)</label>
                <textarea value={voidForm.notes} onChange={e => setVoidForm(f=>({...f,notes:e.target.value}))}
                  rows={2} placeholder="Additional details..."
                  style={{width:'100%',padding:'9px 12px',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'13px',boxSizing:'border-box',resize:'vertical'}} />
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={submitVoid}
                  style={{flex:1,padding:'10px',background:'#4B5563',color:'white',border:'none',borderRadius:'7px',fontSize:'14px',fontWeight:700,cursor:'pointer'}}>
                  ⊘ Void Invoice
                </button>
                <button onClick={() => setVoidModal(null)}
                  style={{flex:1,padding:'10px',background:'white',color:'#6B7280',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'14px',cursor:'pointer'}}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {cancelModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000,padding:'20px'}}>
          <div style={{background:'white',borderRadius:'12px',width:'480px',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3 style={{margin:0,fontSize:'17px',fontWeight:700,color:'#1A3A2A'}}>⚠️ Cancel Invoice {cancelModal.invoice_number}</h3>
              <button onClick={() => setCancelModal(null)} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'#6B7280'}}>✕</button>
            </div>
            <div style={{padding:'20px 24px'}}>
              <p style={{margin:'0 0 16px',fontSize:'13px',color:'#6B7280'}}>This invoice will be marked as cancelled. The record is kept for audit purposes. You can issue a new corrected invoice if needed.</p>
              <div style={{marginBottom:'14px'}}>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Reason *</label>
                <select value={cancelForm.reason} onChange={e => setCancelForm(f=>({...f,reason:e.target.value}))}
                  style={{width:'100%',padding:'9px 12px',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'14px'}}>
                  <option value="">— Select reason —</option>
                  <option value="client_request">Client Request</option>
                  <option value="service_not_rendered">Service Not Rendered</option>
                  <option value="billing_dispute">Billing Dispute</option>
                  <option value="replaced_by_new">Replaced by New Invoice</option>
                  <option value="data_entry_error">Data Entry Error</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{marginBottom:'20px'}}>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,color:'#374151',marginBottom:'6px'}}>Notes (optional)</label>
                <textarea value={cancelForm.notes} onChange={e => setCancelForm(f=>({...f,notes:e.target.value}))}
                  rows={2} placeholder="Additional details..."
                  style={{width:'100%',padding:'9px 12px',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'13px',boxSizing:'border-box',resize:'vertical'}} />
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={submitCancel}
                  style={{flex:1,padding:'10px',background:'#EA580C',color:'white',border:'none',borderRadius:'7px',fontSize:'14px',fontWeight:700,cursor:'pointer'}}>
                  Cancel Invoice
                </button>
                <button onClick={() => setCancelModal(null)}
                  style={{flex:1,padding:'10px',background:'white',color:'#6B7280',border:'1px solid #D1D5DB',borderRadius:'7px',fontSize:'14px',cursor:'pointer'}}>
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF VIEWER MODAL */}
      {showPdfModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:3000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={e => { if(e.target===e.currentTarget){ if(pdfUrl) URL.revokeObjectURL(pdfUrl); setShowPdfModal(false); setPdfUrl(null); }}}>
          <div style={{ background:'#FFF', borderRadius:'12px', width:'100%', maxWidth:'900px',
            height:'90vh', display:'flex', flexDirection:'column', overflow:'hidden',
            boxShadow:'0 25px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ padding:'16px 24px', background:'linear-gradient(135deg, #1F4027, #2D5C3F)',
              display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div>
                <h2 style={{ margin:0, color:'#FFF', fontSize:'17px', fontWeight:'700' }}>Invoice PDF</h2>
                <p style={{ margin:'3px 0 0', color:'#D4AF37', fontSize:'13px' }}>{selectedInvoice?.invoice_number}</p>
              </div>
              <div style={{ display:'flex', gap:'8px' }}>
                {pdfUrl && (
                  <a href={pdfUrl} download={`Invoice_${selectedInvoice?.invoice_number}.pdf`}
                    style={{ background:'#D4AF37', color:'#1A3A2A', padding:'8px 16px', borderRadius:'6px',
                      fontSize:'13px', fontWeight:'700', textDecoration:'none' }}>
                    ⬇ Download
                  </a>
                )}
                <button onClick={() => { if(pdfUrl) URL.revokeObjectURL(pdfUrl); setShowPdfModal(false); setPdfUrl(null); }}
                  style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)',
                    color:'#FFF', padding:'8px 16px', borderRadius:'6px', cursor:'pointer', fontSize:'14px', fontWeight:'600' }}>
                  ✕ Close
                </button>
              </div>
            </div>
            <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#F8FAFC' }}>
              {pdfLoading ? (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                  justifyContent:'center', height:'100%', gap:'16px' }}>
                  <div style={{ width:'44px', height:'44px', border:'4px solid #E5E7EB',
                    borderTopColor:'#2D5C3F', borderRadius:'50%', animation:'spin 1s linear infinite' }} />
                  <p style={{ color:'#6B7280', fontSize:'14px' }}>Generating invoice PDF...</p>
                </div>
              ) : pdfUrl ? (
                <iframe src={pdfUrl} style={{ flex:1, border:'none', width:'100%', height:'100%' }}
                  title="Invoice PDF" />
              ) : null}
            </div>
          </div>
        </div>
      )}
      {/* CREDIT MEMO MODAL */}
      {showCMModal && cmInvoice && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={e => { if(e.target===e.currentTarget) setShowCMModal(false); }}>
          <div style={{ background:'white', borderRadius:'12px', width:'100%', maxWidth:'520px',
            boxShadow:'0 25px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            {/* Header */}
            <div style={{ padding:'20px 24px', background:'linear-gradient(135deg, #7F1D1D, #DC2626)',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h2 style={{ margin:0, color:'white', fontSize:'17px', fontWeight:'700' }}>Issue Credit Memo</h2>
                <p style={{ margin:'3px 0 0', color:'rgba(255,255,255,0.75)', fontSize:'13px' }}>
                  Referencing {cmInvoice.invoice_number} · ${parseFloat(cmInvoice.total_amount).toFixed(2)}
                </p>
              </div>
              <button onClick={() => setShowCMModal(false)}
                style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)',
                  color:'white', padding:'7px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding:'24px' }}>
              {/* Original invoice info */}
              <div style={{ padding:'12px 16px', background:'#FEF2F2', borderRadius:'8px',
                border:'1px solid #FECACA', marginBottom:'20px' }}>
                <p style={{ margin:0, fontSize:'13px', color:'#7F1D1D' }}>
                  <strong>⚠️ Warning:</strong> Issuing a credit memo will mark <strong>{cmInvoice.invoice_number}</strong> as
                  credited and cannot be undone. A new corrected invoice can be issued separately if needed.
                </p>
              </div>

              {/* Reason type */}
              <div style={{ marginBottom:'16px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#555', display:'block', marginBottom:'6px' }}>Reason Type *</label>
                <select style={{ width:'100%', padding:'10px 12px', border:'1px solid #DDD', borderRadius:'6px', fontSize:'14px' }}
                  value={cmForm.reason_type} onChange={e => setCmForm(p => ({ ...p, reason_type: e.target.value }))}>
                  <option value="error_invoice">Error in Invoice</option>
                  <option value="service_not_rendered">Service Not Rendered</option>
                  <option value="partial_discount">Partial Discount / Adjustment</option>
                  <option value="duplicate">Duplicate Invoice</option>
                  <option value="client_complaint">Client Complaint</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Reason notes */}
              <div style={{ marginBottom:'16px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#555', display:'block', marginBottom:'6px' }}>Reason Details *</label>
                <textarea style={{ width:'100%', padding:'10px 12px', border:'1px solid #DDD', borderRadius:'6px',
                  fontSize:'14px', resize:'vertical', minHeight:'80px', boxSizing:'border-box' }}
                  placeholder="Describe the reason for this credit memo..."
                  value={cmForm.reason_notes} onChange={e => setCmForm(p => ({ ...p, reason_notes: e.target.value }))} />
              </div>

              {/* Full or partial */}
              <div style={{ marginBottom:'16px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#555', display:'block', marginBottom:'8px' }}>Credit Amount</label>
                <div style={{ display:'flex', gap:'12px', marginBottom:'10px' }}>
                  {[{ val: true, label: `Full Credit — $${parseFloat(cmInvoice.total_amount).toFixed(2)}` },
                    { val: false, label: 'Partial Credit' }].map(opt => (
                    <div key={String(opt.val)} onClick={() => setCmForm(p => ({ ...p, is_full_credit: opt.val, amount: opt.val ? cmInvoice.total_amount : '' }))}
                      style={{ flex:1, padding:'10px 14px', borderRadius:'8px', cursor:'pointer', textAlign:'center',
                        border: `2px solid ${cmForm.is_full_credit === opt.val ? '#DC2626' : '#E5E7EB'}`,
                        background: cmForm.is_full_credit === opt.val ? '#FEF2F2' : '#FAFAFA',
                        fontSize:'13px', fontWeight:'600',
                        color: cmForm.is_full_credit === opt.val ? '#DC2626' : '#6B7280' }}>
                      {opt.label}
                    </div>
                  ))}
                </div>
                {!cmForm.is_full_credit && (
                  <input type="number" min="0.01" max={cmInvoice.total_amount} step="0.01"
                    style={{ width:'100%', padding:'10px 12px', border:'1px solid #DDD', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box' }}
                    placeholder={`Max: $${parseFloat(cmInvoice.total_amount).toFixed(2)}`}
                    value={cmForm.amount} onChange={e => setCmForm(p => ({ ...p, amount: e.target.value }))} />
                )}
              </div>

              {/* Credit Disposition */}
              <div style={{ marginBottom:'16px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#555', display:'block', marginBottom:'8px' }}>
                  What will happen with this credit? *
                </label>
                {[
                  { val: 'apply_next', icon: '📋', label: 'Apply to next invoice', desc: 'Credit will be available to apply to the next invoice for this client' },
                  { val: 'refund', icon: '💵', label: 'Refund to client', desc: 'Client will receive a refund payment' },
                ].map(opt => (
                  <div key={opt.val} onClick={() => setCmForm(p => ({ ...p, disposition: opt.val }))}
                    style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px',
                      borderRadius:'8px', cursor:'pointer', marginBottom:'8px',
                      border: `2px solid ${cmForm.disposition === opt.val ? '#DC2626' : '#E5E7EB'}`,
                      background: cmForm.disposition === opt.val ? '#FEF2F2' : '#FAFAFA' }}>
                    <span style={{ fontSize:'20px' }}>{opt.icon}</span>
                    <div>
                      <div style={{ fontWeight:'700', fontSize:'13px', color: cmForm.disposition === opt.val ? '#DC2626' : '#374151' }}>{opt.label}</div>
                      <div style={{ fontSize:'11px', color:'#9CA3AF', marginTop:'2px' }}>{opt.desc}</div>
                    </div>
                    <div style={{ marginLeft:'auto', width:'18px', height:'18px', borderRadius:'50%',
                      border: `2px solid ${cmForm.disposition === opt.val ? '#DC2626' : '#D1D5DB'}`,
                      background: cmForm.disposition === opt.val ? '#DC2626' : 'white',
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {cmForm.disposition === opt.val && <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'white' }} />}
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', paddingTop:'16px', borderTop:'1px solid #E5E7EB' }}>
                <button onClick={() => setShowCMModal(false)}
                  style={{ padding:'10px 20px', background:'#F3F4F6', color:'#374151', border:'none',
                    borderRadius:'7px', fontWeight:'600', cursor:'pointer', fontSize:'14px' }}>
                  Cancel
                </button>
                <button onClick={createCreditMemo} disabled={cmSaving}
                  style={{ padding:'10px 24px', background: cmSaving ? '#9CA3AF' : '#DC2626',
                    color:'white', border:'none', borderRadius:'7px', fontWeight:'600',
                    cursor: cmSaving ? 'not-allowed' : 'pointer', fontSize:'14px' }}>
                  {cmSaving ? 'Issuing...' : '📋 Issue Credit Memo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REFUND MODAL */}
      {showRefundModal && refundCM && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:3000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={e => { if(e.target===e.currentTarget) setShowRefundModal(false); }}>
          <div style={{ background:'white', borderRadius:'12px', width:'100%', maxWidth:'440px',
            boxShadow:'0 25px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ padding:'18px 24px', background:'linear-gradient(135deg, #065F46, #16A34A)',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h2 style={{ margin:0, color:'white', fontSize:'16px', fontWeight:'700' }}>Mark as Refunded</h2>
                <p style={{ margin:'3px 0 0', color:'rgba(255,255,255,0.75)', fontSize:'13px' }}>
                  {refundCM.cm_number} · ${parseFloat(refundCM.amount).toFixed(2)}
                </p>
              </div>
              <button onClick={() => setShowRefundModal(false)}
                style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)',
                  color:'white', padding:'6px 12px', borderRadius:'6px', cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ padding:'24px' }}>
              <div style={{ marginBottom:'16px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#555', display:'block', marginBottom:'6px' }}>Refund Method *</label>
                <select style={{ width:'100%', padding:'10px 12px', border:'1px solid #DDD', borderRadius:'6px', fontSize:'14px' }}
                  value={refundForm.refund_method} onChange={e => setRefundForm(p => ({ ...p, refund_method: e.target.value }))}>
                  <option value="check">Check</option>
                  <option value="wire_transfer">Wire Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card Reversal</option>
                  <option value="zelle">Zelle</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ marginBottom:'20px' }}>
                <label style={{ fontSize:'12px', fontWeight:'600', color:'#555', display:'block', marginBottom:'6px' }}>Notes / Reference</label>
                <input style={{ width:'100%', padding:'10px 12px', border:'1px solid #DDD', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box' }}
                  placeholder="Check #, transaction reference..."
                  value={refundForm.refund_notes} onChange={e => setRefundForm(p => ({ ...p, refund_notes: e.target.value }))} />
              </div>
              <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
                <button onClick={() => setShowRefundModal(false)}
                  style={{ padding:'10px 20px', background:'#F3F4F6', color:'#374151', border:'none', borderRadius:'7px', fontWeight:'600', cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={markRefunded}
                  style={{ padding:'10px 24px', background:'#16A34A', color:'white', border:'none',
                    borderRadius:'7px', fontWeight:'600', cursor:'pointer' }}>
                  ✅ Confirm Refund
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>

  );
}
